import { useContext } from 'react'
import swal from 'sweetalert2'

import { digObject } from '@domain-group/javascript-utils'

import {
  Button, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import type { SidebarModalProps } from '@domain-group/suit-theme'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useManageEntitySupportTeamModal from './hooks/useManageEntitySupportTeamModal'
import type { UseManageEntitySupportTeamPayload } from './hooks/useManageEntitySupportTeamModal'

import SupportUserListItem from './SupportUserListItem'

interface ModalCallbacks {
  callbacks: {
    closeModal: () => void,
    updateEntity: (payload: HandleCallbackActionParams) => Promise<object>,
  },
}

interface ModalContextInterface {
  modalData: {
    ManageEntitySupportTeamModal: {
      callbacks: {
        updateEntity: Function,
      },
      entity: object,
    },
  },
}

const confirmSave = (entityState: object, props: ModalCallbacks, updateFn: Function) => {
  const { callbacks: { closeModal, updateEntity } } = props

  const entityPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: {
      include_agency_support_team_users: true,
      include_user_support_team_users: true,
    },
    toastText: 'Support Users Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: 'Yes, update it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Update Support Users?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        updateEntity(entityPayload)
      }
    })
}

const ManageEntitySupportTeamModal = (props: SidebarModalProps & ModalCallbacks) => {
  const { callbacks, modalKey = 'ManageEntitySupportTeamModal', showModal } = props

  const modalContext: ModalContextInterface = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { entity, callbacks: payloadCallbacks } = modalPayload
  const { updateEntity: updateFn } = payloadCallbacks || {}

  // @ts-expect-error TS2322
  const payload: UseManageEntitySupportTeamPayload = useManageEntitySupportTeamModal(entity)

  const {
    callbacks: {
      toggleSupportUserId,
    },
    entityParams,
    hasSelectedSupportUsers,
    selectedSupportUserIds,
    sortedSupportUsers,
    updating,
  } = payload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Support" titleSecondLine="Team" />

      <SidebarModal.Content>
        {/* @ts-expect-error */}
        <EntitySelector
          callbacks={{ selectItem: user => toggleSupportUserId(user?.id) }}
          entityKey="users"
          entityTitleKey="full_name"
          label="Support User"
          searchRequestOptions={{ admin_users_only: true }}
          selectedIds={selectedSupportUserIds}
          showSelectedEntity={false}
        />

        {hasSelectedSupportUsers && (
          <>
            <SectionDivider marginTop="large">Selected</SectionDivider>

            {sortedSupportUsers.map(user => (
              <SupportUserListItem
                callbacks={{
                  toggleSupportUserId: () => toggleSupportUserId(user.id),
                }}
                key={user.id}
                user={user}
              />
            ))}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => confirmSave(entityParams, props, updateFn)}
          size="large"
        >
          Save Support Users
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

// @ts-expect-error TS7006
const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <ManageEntitySupportTeamModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
